import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';

import {
  createCategory,
  getCategory,
  getRewardGallery,
  getSchedules,
  updateCategory,
} from '../utils/api';
import { LoadingIndicator } from './assets';
import WeeklyScheduleEditor from './WeeklyScheduleEditor';

const emptyForm = (name = '') => ({
  name,
  color: null,
  image_id: null,
  schedules: [],
});

const sectionSx = (theme) => ({
  p: { xs: 1.5, sm: 2 },
  border: `2px solid ${alpha(theme.palette.primary.main, 0.42)}`,
  borderRadius: 3,
  background: `linear-gradient(145deg, ${lighten(theme.palette.background.paper, 0.07)}, ${theme.palette.background.paper})`,
  boxShadow: `inset 0 0 20px rgba(0, 0, 0, 0.28), 0 0 14px ${alpha(theme.palette.primary.main, 0.08)}`,
});

export default function CategoryEditorDialog({
  open,
  categoryId = null,
  initialName = '',
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm(initialName));
  const [images, setImages] = useState([]);
  const [backgroundSchedules, setBackgroundSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    setError('');
    Promise.all([
      categoryId ? getCategory(categoryId) : Promise.resolve(emptyForm(initialName)),
      getRewardGallery({ limit: 100 }),
      getSchedules(),
    ])
      .then(([category, gallery, allSchedules]) => {
        if (!alive) return;
        setForm({
          name: category.name || initialName,
          color: category.color || null,
          image_id: category.image_id || null,
          schedules: (category.schedules || []).map((schedule) => ({ ...schedule })),
        });
        const galleryImages = gallery.images || [];
        setImages(
          category.image && !galleryImages.some((image) => image.id === category.image.id)
            ? [category.image, ...galleryImages]
            : galleryImages,
        );
        setBackgroundSchedules((allSchedules || []).filter((schedule) => (
          categoryId == null || String(schedule.category_id) !== String(categoryId)
        )));
      })
      .catch((err) => {
        if (alive) setError(err.data?.message || err.message || 'Unable to load category settings.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [categoryId, initialName, open]);

  const save = async () => {
    setSaving(true);
    setError('');
    const payload = {
      name: form.name,
      color: form.color,
      image_id: form.image_id,
      schedules: form.schedules.map(({ days, start, end }) => ({ days, start, end })),
    };
    try {
      const saved = categoryId
        ? await updateCategory(categoryId, payload)
        : await createCategory(payload);
      onSaved(saved);
    } catch (err) {
      setError(err.data?.message || err.message || 'Unable to save category.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          sx: (theme) => ({
            overflow: 'hidden',
            border: `2px solid ${theme.palette.primary.main}`,
            borderRadius: 4,
            color: theme.palette.primary.main,
            background:
              `linear-gradient(145deg, ${lighten(theme.palette.background.paper, 0.1)} 0%, ` +
              `${theme.palette.background.paper} 58%, ${darken(theme.palette.secondary.main, 0.84)} 100%)`,
            boxShadow:
              `0 8px 0 ${darken(theme.palette.primary.main, 0.56)}, ` +
              `0 0 30px ${alpha(theme.palette.primary.main, 0.35)}`,
          }),
        },
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          px: { xs: 2, sm: 3 },
          py: 2,
          color: theme.palette.primary.main,
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
          background: `linear-gradient(90deg, ${darken(theme.palette.primary.main, 0.8)}, ${theme.palette.background.paper} 68%)`,
          textShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.65)}`,
        })}
      >
        {categoryId ? 'Edit Category' : 'Create Category'}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: '24px !important' }}>
        {loading ? <LoadingIndicator text="loading category" /> : (
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Category name"
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              autoFocus
              fullWidth
              sx={(theme) => ({
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: theme.palette.background.default,
                  boxShadow: `inset 0 0 16px rgba(0, 0, 0, 0.35)`,
                },
              })}
            />

            <Box sx={sectionSx}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Category color</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Category color"
                  type="color"
                  value={form.color || '#00e5ff'}
                  onInput={(event) => setForm((current) => ({ ...current, color: event.target.value }))}
                  onChange={(event) => setForm((current) => ({ ...current, color: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={(theme) => ({
                    width: { xs: '100%', sm: 220 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      backgroundColor: theme.palette.background.default,
                    },
                    '& input': {
                      minHeight: 42,
                      cursor: 'pointer',
                    },
                  })}
                />
                <Button
                  type="button"
                  disabled={!form.color}
                  onClick={() => setForm((current) => ({ ...current, color: null }))}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
                >
                  Remove color
                </Button>
                <Typography variant="caption" sx={{ color: form.color || 'text.secondary' }}>
                  {form.color ? form.color.toUpperCase() : 'No color selected'}
                </Typography>
              </Stack>
            </Box>

            <Box sx={sectionSx}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Category image</Typography>
              <Grid container spacing={1} sx={{ maxHeight: 260, overflowY: 'auto' }}>
                <Grid size={{ xs: 4, sm: 3 }}>
                  <Button
                    fullWidth
                    variant={form.image_id === null ? 'contained' : 'outlined'}
                    onClick={() => setForm((current) => ({ ...current, image_id: null }))}
                    sx={(theme) => ({
                      m: 0,
                      aspectRatio: '1 / 1',
                      borderRadius: 3,
                      borderColor: form.image_id === null
                        ? (theme.palette.green?.main || '#39FF14')
                        : theme.palette.primary.main,
                      backgroundColor: theme.palette.background.default,
                      boxShadow: form.image_id === null
                        ? `0 0 14px ${alpha(theme.palette.green?.main || '#39FF14', 0.28)}`
                        : 'none',
                    })}
                  >
                    No image
                  </Button>
                </Grid>
                {images.map((image) => (
                  <Grid key={image.id} size={{ xs: 4, sm: 3 }}>
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Select reward image ${image.id}`}
                      onClick={() => setForm((current) => ({ ...current, image_id: image.id }))}
                      sx={(theme) => ({
                        p: 0,
                        width: '100%',
                        aspectRatio: '1 / 1',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '3px solid',
                        borderColor: form.image_id === image.id
                          ? (theme.palette.green?.main || '#39FF14')
                          : alpha(theme.palette.primary.main, 0.4),
                        borderRadius: 3,
                        bgcolor: theme.palette.background.default,
                        boxShadow: form.image_id === image.id
                          ? `0 0 16px ${alpha(theme.palette.green?.main || '#39FF14', 0.45)}`
                          : 'none',
                        transition: 'transform 130ms ease, border-color 130ms ease, box-shadow 130ms ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 14px ${alpha(theme.palette.primary.main, 0.32)}`,
                        },
                      })}
                    >
                      <Box
                        component="img"
                        src={image.thumbnail_url}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={sectionSx}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Category schedule</Typography>
              <WeeklyScheduleEditor
                schedules={form.schedules}
                onChange={(schedules) => setForm((current) => ({ ...current, schedules }))}
                createSchedule={({ id, day, start, end }) => ({
                  id,
                  days: [day],
                  start,
                  end,
                })}
                backgroundSchedules={backgroundSchedules}
                getScheduleColor={(schedule) => schedule.category_color || form.color || '#00E5FF'}
                getScheduleLabel={(schedule) => (
                  schedule.category === 'no-category' ? 'No category' : schedule.category
                )}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <Button onClick={onClose} disabled={saving} sx={{ m: 0 }}>Cancel</Button>
        <Button
          onClick={save}
          disabled={loading || saving || !form.name.trim()}
          variant="contained"
          sx={(theme) => ({
            m: 0,
            color: theme.palette.background.default,
            borderColor: theme.palette.primary.main,
            background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${darken(theme.palette.primary.main, 0.25)})`,
            boxShadow: `0 5px 0 ${darken(theme.palette.primary.main, 0.55)}, 0 0 16px ${alpha(theme.palette.primary.main, 0.4)}`,
            '&:hover': {
              background: `linear-gradient(180deg, ${lighten(theme.palette.primary.main, 0.12)}, ${darken(theme.palette.primary.main, 0.18)})`,
              transform: 'translate(-1px, -1px)',
              boxShadow: `1px 6px 0 ${darken(theme.palette.primary.main, 0.55)}, 0 0 22px ${alpha(theme.palette.primary.main, 0.5)}`,
            },
            '&:active': {
              transform: 'translateY(3px)',
              boxShadow: `0 2px 0 ${darken(theme.palette.primary.main, 0.55)}`,
            },
          })}
        >
          {saving ? 'Saving…' : 'Save category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
